/**
 * Export Service
 *
 * Handles exporting user data in various scopes:
 * - Library only (POIs + Tags)
 * - Library + All Trips (full takeout)
 * - Single Trip (trip with referenced POIs)
 */

import { api } from './api';
import type {
  POIPlannerExport,
  ExportTrip,
  ExportDay,
  ExportScope,
  ExportOptions,
  POI,
  Tag,
  TransportEvent,
  AccommodationConfig,
} from '@poi-planner/shared';

// ── Error Handling ──

class ExportError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ExportError';
  }
}

// ── Export Functions ──

/**
 * Fetch all POIs from the library
 */
async function fetchLibraryPois(): Promise<POI[]> {
  const library = await api.loadLibrary();
  return library.pois;
}

/**
 * Fetch all tags from the library
 */
async function fetchLibraryTags(): Promise<Tag[]> {
  return api.loadTags();
}

/**
 * Fetch trip data for export
 */
async function fetchTripForExport(tripId: string): Promise<ExportTrip> {
  const tripData = await api.loadTripById(tripId);
  if (!tripData) {
    throw new ExportError('NOT_FOUND', 'Trip not found');
  }

  // Convert to export format
  const days: ExportDay[] = tripData.schedule.days.map((day) => ({
    id: day.id,
    date: day.date,
    timezone: day.timezone,
    wakeUpTime: day.wakeUpTime,
    scheduledPOIs: day.scheduledPOIs.map((sp, idx) => ({
      poiId: sp.poiId,
      startTime: sp.startTime,
      order: idx,
    })),
    sleepPoiId: day.sleep?.poiId ?? null,
  }));

  const transportEvents: TransportEvent[] = tripData.schedule.transportEvents.map((te) => ({
    id: te.id,
    mode: te.mode,
    origin: te.origin,
    destination: te.destination,
    departureTime: te.departureTime,
    duration: te.duration,
    reference: te.reference,
    notes: te.notes,
    dayId: te.dayId,
    order: te.order,
  }));

  // accommodationConfigs is already a Record in schedule
  const accommodationConfigs: Record<string, AccommodationConfig> = {};
  for (const [poiId, config] of Object.entries(tripData.schedule.accommodationConfigs || {})) {
    accommodationConfigs[poiId] = {
      color: config.color,
      checkInTime: config.checkInTime ?? undefined,
      checkOutTime: config.checkOutTime ?? undefined,
    };
  }

  return {
    trip: {
      id: tripData.trip.id,
      name: tripData.trip.name,
      createdAt: tripData.trip.createdAt,
      updatedAt: tripData.trip.updatedAt,
    },
    schedule: {
      days,
      transportEvents,
      accommodationConfigs,
    },
  };
}

/**
 * Fetch all accessible trips for export
 */
async function fetchAllTripsForExport(): Promise<ExportTrip[]> {
  const trips = await api.listTrips();
  const exportTrips: ExportTrip[] = [];

  for (const trip of trips) {
    const exportTrip = await fetchTripForExport(trip.id);
    exportTrips.push(exportTrip);
  }

  return exportTrips;
}

/**
 * Generate export data based on scope
 */
export async function generateExport(options: ExportOptions): Promise<POIPlannerExport> {
  const exportData: POIPlannerExport = {
    version: 1,
    exportedAt: new Date().toISOString(),
  };

  switch (options.scope) {
    case 'library': {
      // Library only
      const pois = await fetchLibraryPois();
      const tags = await fetchLibraryTags();
      exportData.library = { pois, tags };
      break;
    }

    case 'all': {
      // Full takeout: library + all trips
      const pois = await fetchLibraryPois();
      const tags = await fetchLibraryTags();
      const trips = await fetchAllTripsForExport();
      exportData.library = { pois, tags };
      exportData.trips = trips;
      break;
    }

    case 'trip': {
      // Single trip with referenced POIs
      if (!options.tripId) {
        throw new ExportError('MISSING_TRIP_ID', 'tripId is required for trip export');
      }

      const trip = await fetchTripForExport(options.tripId);
      const tags = await fetchLibraryTags();

      // Collect all referenced POI IDs
      const poiIds = new Set<string>();
      for (const day of trip.schedule.days) {
        for (const sp of day.scheduledPOIs) {
          poiIds.add(sp.poiId);
        }
        if (day.sleepPoiId) {
          poiIds.add(day.sleepPoiId);
        }
      }
      for (const config of Object.keys(trip.schedule.accommodationConfigs)) {
        poiIds.add(config);
      }

      // Fetch only referenced POIs
      const allPois = await fetchLibraryPois();
      const referencedPois = allPois.filter((p) => poiIds.has(p.id));

      exportData.library = { pois: referencedPois, tags };
      exportData.trips = [trip];
      break;
    }
  }

  return exportData;
}

/**
 * Download export as JSON file
 */
export async function downloadExport(options: ExportOptions): Promise<void> {
  const data = await generateExport(options);

  // Generate filename
  const date = new Date().toISOString().split('T')[0];
  let filename = `poi-planner-export-${date}`;
  if (options.scope === 'trip' && data.trips?.[0]) {
    const tripName = data.trips[0].trip.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    filename = `poi-planner-${tripName}-${date}`;
  }
  filename += '.json';

  // Create blob and download
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
